/**
 * TicketFormatoEjemploModal — Muestra cómo se vería/imprimiría el ticket de
 * venta en los 3 formatos térmicos usando UNA VENTA DE EJEMPLO real.
 *
 * QUÉ HACE:
 *  - Genera la venta demo (fixture `ticketFormatoDemo`) y la pasa por los
 *    MISMOS builders de la impresión real (`buildSaleTicketPreviewLines`) con
 *    los datos del negocio actual (`loadBusinessForReceipt`), logo/QR incluidos.
 *  - Permite cambiar formato (Completo / Compacto / Ultra) y ancho (80 / 58 mm)
 *    en una sola tira, o comparar las 3 tiras lado a lado («Ver los 3 juntos»).
 *  - Botón «Imprimir ejemplo»: imprime en la impresora térmica configurada
 *    (misma ruta `tryPrintThermalSaleReceipt` que el recibo real) con el ancho
 *    REAL de la impresora y la nota «EJEMPLO» visible en el papel.
 *
 * QUÉ EXPORTA: TicketFormatoEjemploModal (named).
 *
 * QUÉ NO DEBE HACER:
 *  - No guarda la venta demo ni toca el carrito/caja real.
 *  - No activa `forzarFormatoEnPreview`: como la venta demo es estándar (sin
 *    puntos/crédito/ajustes), Compacto/Ultra aplican también en la impresión.
 *  - No imprime nada si no hay impresora disponible (avisa para configurarla).
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  X,
  Printer,
  Columns2,
  Rows3,
  Loader2,
  Eye,
  AlertTriangle,
} from "lucide-react";
import type { TicketFormato } from "../utils/businessConfigStorage";
import {
  buildSaleTicketPreviewLines,
  loadBusinessForReceipt,
  resolveTicketQrUrl,
} from "../utils/saleTicketEscPos";
import type { ThermalPreviewLine } from "../utils/escposLayout";
import { ThermalTicketSimulator } from "./ThermalTicketSimulator";
import {
  TICKET_PREVIEW_SURFACE_CLASS,
  TICKET_PREVIEW_MODAL_FRAME_CLASS,
} from "../constants/ticketTornPaper";
import { SAFE_AREA } from "@/lib/nativeShell";
import {
  canPrintOnCurrentPlatform,
  tryPrintThermalSaleReceipt,
} from "@/lib/native/posHardware";
import {
  isPosPrinterConfigured,
  loadPosPrinterConfig,
} from "@/lib/native/posPrinterConfig";
import { nativeErrorMessage } from "../utils/userSettingsPosHardware.helpers";
import { buildTicketFormatoDemoPayload } from "../fixtures/ticketFormatoDemo";

const FORMATOS: Array<{ value: TicketFormato; label: string }> = [
  { value: "completo", label: "Completo" },
  { value: "compacto", label: "Compacto" },
  { value: "ultracompacto", label: "Ultra" },
];

const FORMATO_LABEL: Record<TicketFormato, string> = {
  completo: "Completo",
  compacto: "Compacto",
  ultracompacto: "Ultra",
};

interface TicketFormatoEjemploModalProps {
  /** Formato con el que abre la demo (el de la tarjeta pulsada). */
  initialFormato: TicketFormato;
  onClose: () => void;
}

export function TicketFormatoEjemploModal({
  initialFormato,
  onClose,
}: TicketFormatoEjemploModalProps) {
  const [formato, setFormato] = useState<TicketFormato>(initialFormato);
  const [paperWidthMm, setPaperWidthMm] = useState<58 | 80>(80);
  const [comparar, setComparar] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printReady, setPrintReady] = useState(false);

  // Si reabren la demo desde otra tarjeta sin cerrar el modal, respeta el nuevo.
  useEffect(() => {
    setFormato(initialFormato);
  }, [initialFormato]);

  // La venta demo se construye UNA vez (catálogo + descuentos + servicio).
  const demoPayload = useMemo(() => buildTicketFormatoDemoPayload(), []);

  // Mismos datos de negocio que la impresión real → vista 1:1 con el papel.
  const biz = useMemo(() => loadBusinessForReceipt(), []);
  const qrUrl = useMemo(() => resolveTicketQrUrl(biz) || undefined, [biz]);
  const qrLabel = biz.ticketQrLabel?.trim() || undefined;

  // Disponibilidad real de impresión al abrir (papel del ancho configurado).
  useEffect(() => {
    let cancelled = false;
    void loadPosPrinterConfig().then((cfg) => {
      if (cancelled) return;
      setPrintReady(canPrintOnCurrentPlatform() && isPosPrinterConfigured(cfg));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Líneas de la tira única (formato + ancho actuales). */
  const single = useMemo(() => {
    if (!demoPayload)
      return { lines: [] as string[], preview: [] as ThermalPreviewLine[] };
    const preview = buildSaleTicketPreviewLines(
      { ...demoPayload, paperWidthMm, formatoTicket: formato },
      biz,
    );
    return { lines: preview.map((l) => l.text), preview };
  }, [demoPayload, paperWidthMm, formato, biz]);

  /** Las 3 tiras para el modo comparación (mismo ancho de papel). */
  const compare = useMemo(() => {
    if (!demoPayload) return [];
    return FORMATOS.map((f) => {
      const preview = buildSaleTicketPreviewLines(
        { ...demoPayload, paperWidthMm, formatoTicket: f.value },
        biz,
      );
      return { label: f.label, lines: preview.map((l) => l.text), preview };
    });
  }, [demoPayload, paperWidthMm, biz]);

  const handlePrint = async () => {
    if (!demoPayload || !printReady || printing) return;
    setPrinting(true);
    try {
      // Imprime con el ancho REAL configurado de la impresora y este formato.
      const ok = await tryPrintThermalSaleReceipt({
        ...demoPayload,
        formatoTicket: formato,
      });
      if (!ok) {
        toast.error(
          "No se pudo imprimir el ejemplo. Configura y guarda la impresora en este panel.",
          { id: "ticket-formato-demo-print-err" },
        );
      }
    } catch (e: unknown) {
      toast.error(
        nativeErrorMessage(e, "Error al imprimir el ejemplo de ticket"),
        { id: "ticket-formato-demo-print-err" },
      );
    } finally {
      setPrinting(false);
    }
  };

  const chipCls = (active: boolean) =>
    `px-2.5 py-1.5 text-[10px] sm:text-xs font-semibold transition-colors ${
      active
        ? "bg-primary-600 text-white"
        : "text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800"
    }`;

  return createPortal(
    <div
      className={`${SAFE_AREA.modalOverlay} fixed inset-0 z-[130] flex items-center justify-center p-1 sm:p-4 animate-fade-in`}
    >
      <button
        type="button"
        aria-label="Cerrar ejemplo"
        className="no-print absolute inset-0 bg-black/60 backdrop-blur-sm border-0 cursor-default"
        onClick={onClose}
      />
      <div
        className="relative z-10 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg lg:max-w-2xl flex flex-col overflow-hidden max-h-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 p-2 sm:p-3 bg-gradient-to-r from-primary-600 to-primary-700 flex items-center justify-between gap-2">
          <h2 className="min-w-0 text-[10px] leading-tight sm:text-sm font-bold text-white flex items-center gap-1.5 sm:gap-2">
            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
            <span className="min-w-0 break-words">
              Ejemplo del ticket térmico
            </span>
          </h2>
          <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="p-1 sm:p-1.5 rounded-full hover:bg-white/20 transition-colors"
            >
              <X size={16} className="text-white" />
            </button>
          </div>
        </div>

        {/* Controles: formato + ancho + comparación */}
        <div className="no-print shrink-0 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex flex-wrap items-center justify-center gap-1.5">
          <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900">
            {FORMATOS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFormato(f.value)}
                className={chipCls(formato === f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900">
            <button
              type="button"
              onClick={() => setPaperWidthMm(80)}
              className={chipCls(paperWidthMm === 80)}
            >
              80mm
            </button>
            <button
              type="button"
              onClick={() => setPaperWidthMm(58)}
              className={chipCls(paperWidthMm === 58)}
            >
              58mm
            </button>
          </div>
          <button
            type="button"
            onClick={() => setComparar((c) => !c)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] sm:text-xs font-semibold transition-colors ${
              comparar
                ? "border-primary-600 bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300"
                : "border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800"
            }`}
          >
            {comparar ? <Rows3 size={13} /> : <Columns2 size={13} />}
            {comparar ? "Una sola tira" : "Ver los 3 juntos"}
          </button>
        </div>

        {/* Aviso de contexto */}
        <div className="no-print shrink-0 px-2 sm:px-3 py-1.5 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800/40 flex items-start gap-1.5">
          <AlertTriangle
            size={12}
            className="shrink-0 mt-px text-amber-600 dark:text-amber-400"
          />
          <p className="text-[10px] text-amber-800 dark:text-amber-200 leading-snug">
            Es una venta de ejemplo con tus datos de negocio — no se guarda en
            caja. Al imprimir sale la nota «EJEMPLO» y se usa el ancho real de
            tu impresora.
          </p>
        </div>

        {/* Tickets */}
        <div className={TICKET_PREVIEW_SURFACE_CLASS}>
          <div className={TICKET_PREVIEW_MODAL_FRAME_CLASS}>
            {!demoPayload ? (
              <p className="py-10 text-xs text-gray-500 dark:text-slate-400 text-center px-4">
                No se pudo armar la venta de ejemplo (catálogo demo vacío).
              </p>
            ) : comparar ? (
              <div className="w-full flex gap-3 overflow-x-auto items-start py-1">
                {compare.map((strip) => (
                  <div
                    key={strip.label}
                    className="shrink-0 flex flex-col items-center gap-1"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                      {strip.label}
                    </span>
                    <ThermalTicketSimulator
                      lines={strip.lines}
                      previewLines={strip.preview}
                      paperWidthMm={paperWidthMm}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <ThermalTicketSimulator
                lines={single.lines}
                previewLines={single.preview}
                paperWidthMm={paperWidthMm}
                qrUrl={qrUrl}
                qrLabel={qrLabel}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="no-print shrink-0 px-2 sm:px-3 py-2 sm:py-2.5 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 flex items-center justify-between gap-2">
          <p className="hidden sm:block text-[10px] text-gray-500 dark:text-slate-400 leading-snug min-w-0">
            {FORMATO_LABEL[formato]} · {paperWidthMm}mm. Compara el gasto de
            papel entre formatos antes de elegir.
          </p>
          <button
            type="button"
            onClick={() => void handlePrint()}
            disabled={!printReady || printing || comparar}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 text-[11px] sm:text-xs font-black uppercase tracking-wide transition-colors ${
              !printReady || comparar
                ? "border-gray-200 dark:border-slate-600 text-gray-400 dark:text-slate-500 cursor-not-allowed"
                : "border-primary-600 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-950/30"
            }`}
            title={
              comparar
                ? "Cierra la comparación para imprimir un solo formato"
                : printReady
                  ? "Imprime el ejemplo en tu impresora térmica"
                  : "Configura y guarda la impresora arriba para imprimir"
            }
          >
            {printing ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Printer size={13} />
            )}
            Imprimir ejemplo
          </button>
        </div>
        {!printReady && (
          <p className="no-print shrink-0 px-3 pb-2 text-[10px] text-gray-400 dark:text-slate-500">
            Para imprimir el ejemplo cierra esta demo, configura y guarda la
            impresora en el panel «POS, gaveta y seguridad» y vuelve a abrirla.
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
